import { create } from "zustand";

import type { AppLanguage } from "@/localization/hooks/use-translation";

const SUPPORTED_LANGUAGES: readonly AppLanguage[] = ["en", "lt"];

export const LANGUAGE_STORAGE_KEY = "ceedero.language";

/**
 * The language the interface is drawn in.
 *
 * Taken from the browser on first load and then owned by the user, whose choice
 * outlives the tab. It is not read from the account: somebody signing in on a
 * borrowed machine should get the language of that machine, not of the last
 * person to use their account — which is also why the choice is stored here
 * rather than sent anywhere.
 */
const readStoredLanguage = () => {
  if (typeof localStorage === "undefined") {
    return undefined;
  }
  const stored: unknown = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return SUPPORTED_LANGUAGES.find((language) => language === stored);
};

const resolveInitialLanguage = (): AppLanguage => {
  const stored = readStoredLanguage();
  if (stored !== undefined) {
    return stored;
  }
  const preferred = typeof navigator === "undefined" ? undefined : navigator.language;
  const base = preferred?.split("-")[0];
  return SUPPORTED_LANGUAGES.find((language) => language === base) ?? "en";
};

interface LanguageState {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
}

export const useLanguageStore = create<LanguageState>((set) => ({
  language: resolveInitialLanguage(),
  setLanguage: (language) => {
    set({ language });
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }
    if (typeof document !== "undefined") {
      // The one place the document's own language is set, so assistive
      // technology announces the page in the language it is actually written in.
      document.documentElement.lang = language;
    }
  },
}));
